import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import UploadedAttachmentList from "../../components/UploadedAttachmentList";
import logo from "../../images/logo.png";

const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "application/vnd.ms-outlook", "application/x-msg", "application/CDFV2", "application/x-ole-storage", "application/octet-stream", ""];

const validateFile = (file) => {
  if (!file) return null;
  if (!allowedTypes.includes(file.type)) {
    alert("Incorrect file format.");
    return null;
  }
  return file;
};

const TaxForm = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const [isSigned, setIsSigned] = useState(false);
  const [isReviewed, setIsReviewed] = useState(false);
  const [w4File, setW4File] = useState(null);
  const [ilW4File, setIlW4File] = useState(null);
  const [files, setFiles] = useState([]); // array from nwt_tax_forms

  // 🔄 Fetch uploaded forms
  const fetchTaxDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/taxform/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setFiles(res.data || []);
    } catch (err) {
      console.error("Fetch tax docs failed", err);
    }
  }, [API_BASE, userId]);

  useEffect(() => {
    fetchTaxDocs();
  }, [fetchTaxDocs]);

  // 🔄 Signature status (UNCHANGED)
  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        const TASK_ID = 5;
        const idKey = String(TASK_ID);
        const nameKey = "tax_form";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        setIsSigned(status === "review" || status === "completed");
        setIsReviewed(status === "completed" || status === "almost_expired");
      } catch (err) {
        console.error("Error checking signature status:", err);
      }
    };

    if (userId) fetchTaskStatus();
  }, [userId, API_BASE]);

  // ⬆ Upload
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!w4File && !ilW4File) {
      alert("Please select at least one file to upload.");
      return;
    }

    const formData = new FormData();
    if (w4File) formData.append("w4_form", w4File);
    if (ilW4File) formData.append("il_w4_form", ilW4File);
    formData.append("userId", userId);

    try {
      await axios.post(
        `${API_BASE}/api/taxform/upload?userId=${userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      alert("Upload successful!");
      setW4File(null);
      setIlW4File(null);
      fetchTaxDocs();
    } catch (err) {
      console.error("Upload failed", err);
      alert("Upload failed");
    }
  };

  const handleCancel = () => {
    navigate(isAdmin && paramId ? `/userhome/${paramId}` : "/userhome");
  };

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>Tax Form</span>
        <button className="btn btn-secondary" onClick={handleCancel}>
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Tax Form</h2>
          </div>
          <CompanyAddress />
        </div>

        <p>
          Forms reference:{" "}
          <a href="/task-files/Form W-4.pdf" target="_blank" rel="noreferrer">
            Form W-4
          </a>{" "}
          |{" "}
          <a
            href="/task-files/Form IL-W-4.pdf"
            target="_blank"
            rel="noreferrer"
          >
            Form IL-W-4
          </a>
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">Upload Form W-4</label>
            <input
              type="file"
              className="form-control mb-3"
              disabled={isReviewed || (!isAdmin && isSigned)}
              onChange={(e) => setW4File(validateFile(e.target.files[0]))}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Upload Form IL-W-4</label>
            <input
              type="file"
              className="form-control mb-3"
              disabled={isReviewed || (!isAdmin && isSigned)}
              onChange={(e) => setIlW4File(validateFile(e.target.files[0]))}
            />
          </div>

          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={(!w4File && !ilW4File) || (isReviewed || (!isAdmin && isSigned))}
            >
              Upload
            </button>
          </div>
        </form>

        {files.length > 0 && (
          <UploadedAttachmentList
            title="Uploaded Version"
            files={files}
            getFileKey={(file) => file.tax_id || file.tax_file_path}
            getFileName={(file) => file.tax_original_name}
            getFileMeta={(file) => file.tax_form_type}
            getFileViewUrl={(file) => `${API_BASE}/uploads/taxforms/${file.tax_file_path}`}
            getFileDownloadName={(file) => file.tax_original_name}
          />
        )}

        {userId && (
          <>
            <SignatureManager
              taskName="tax_form"
              taskId={5}
              userId={userId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="tax_form"
              taskId={5}
              userId={userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default TaxForm;


